import { FolderIcon } from "@heroicons/react/outline";
import { StackIcon } from "@radix-ui/react-icons";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { useUnifiedRegistry } from "../unified-registry-context";
import { getEntityName } from "../utils";

import { useDragDrop } from "./DragDropProvider";

import { Entity, EntityType } from "@/queries/folder-entities/types";
import { EntityIcon } from "@/components/ui/icons";

const DragItemIcon = ({
  multipleSelected,
  activeItem,
}: {
  multipleSelected: boolean;
  activeItem: Entity;
}) => {
  if (multipleSelected) {
    return <StackIcon className="h-4 w-4" />;
  }

  if (activeItem.type === EntityType.FOLDER) {
    return <FolderIcon className="h-4 w-4" />;
  }

  return (
    <div className="opacity-75">
      <EntityIcon type={activeItem.type} />
    </div>
  );
};

const CustomDragOverlay: React.FC = () => {
  const { activeItem, isDragging } = useDragDrop();
  const store = useUnifiedRegistry();
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!isDragging || !activeItem) {
      setIsVisible(false);
      return;
    }

    const showTimer = setTimeout(() => {
      setIsVisible(true);
    }, 10);

    const handleMouseMove = (e: MouseEvent) => {
      setPosition({
        x: e.clientX + 16,
        y: e.clientY + 16,
      });
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (touch) {
        setPosition({
          x: touch.clientX + 16,
          y: touch.clientY + 16,
        });
      }
    };

    const currentPos = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    setPosition({
      x: currentPos.x + 16,
      y: currentPos.y + 16,
    });

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("touchmove", handleTouchMove);

    return () => {
      clearTimeout(showTimer);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("touchmove", handleTouchMove);
      setIsVisible(false);
    };
  }, [isDragging, activeItem]);

  if (!activeItem || !isDragging || !isVisible) {
    return null;
  }

  const itemCount =
    activeItem.type === EntityType.FOLDER && "itemCount" in activeItem
      ? activeItem.itemCount
      : undefined;

  // Check if the dragged item is selected and if there are multiple selections
  const isItemSelected = store.isSelected(activeItem);
  const multipleSelected = isItemSelected && store.selectedCount > 1;
  const selectedCount = multipleSelected ? store.selectedCount : 1;

  // Generate background cards based on the number of selected items
  const generateStackCards = () => {
    const cards = [];
    const maxCards = Math.min(selectedCount - 1, 5); // Show up to 5 background cards, excluding the main card

    for (let i = 0; i < maxCards; i++) {
      const cardIndex = maxCards - i; // Start from the back
      const offset = cardIndex * 2 + 2; // Increase offset for each card
      const rotation = (cardIndex % 2 === 0 ? -1 : 1) * (0.5 + cardIndex * 0.3); // Alternate rotation
      const opacity = Math.max(0.3, 1 - cardIndex * 0.15); // Decrease opacity for cards further back

      cards.push(
        <div
          key={i}
          className={`absolute flex items-center gap-2.5 rounded-lg border px-3 py-2 ${
            cardIndex === 1 ? "border-blue-200 bg-blue-50" : "border-blue-200 bg-blue-50"
          }`}
          style={{
            top: `-${offset}px`,
            left: `${offset}px`,
            width: `calc(100% - ${offset * 2}px)`,
            transform: `rotate(${rotation}deg)`,
            opacity: opacity,
          }}
        >
          <div className="h-7 w-7 rounded-md bg-gray-100" />
          <div className="flex-1">
            <div className={`h-3 rounded bg-gray-100 ${cardIndex === 1 ? "w-20" : "w-24"}`} />
            <div className={`mt-1 h-2 rounded bg-gray-100 ${cardIndex === 1 ? "w-14" : "w-16"}`} />
          </div>
        </div>,
      );
    }

    return cards;
  };

  const adjustedPosition = {
    x: Math.min(position.x, window.innerWidth - 250),
    y: Math.min(position.y, window.innerHeight - 80),
  };

  const overlayContent = (
    <AnimatePresence mode="wait">
      {isVisible && (
        <motion.div
          initial={{
            scale: 0.8,
            opacity: 0,
            y: -10,
            filter: "blur(4px)",
          }}
          animate={{
            scale: 1,
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
          }}
          exit={{
            scale: 0.9,
            opacity: 0,
            y: 5,
            filter: "blur(2px)",
          }}
          transition={{
            duration: 0.2,
            ease: [0.32, 0.72, 0, 1],
            opacity: { duration: 0.15 },
            scale: { type: "spring", damping: 25, stiffness: 300 },
          }}
          className="pointer-events-none fixed z-[10000]"
          style={{
            left: `${adjustedPosition.x - 18}px`,
            top: `${adjustedPosition.y - 18}px`,
            transform: "translate(0, 0)",
          }}
        >
          <motion.div
            className="relative"
            animate={{
              rotate: [0, 1, 0],
            }}
            transition={{
              duration: 0.3,
              ease: "easeOut",
            }}
          >
            {/* Stack multiple cards if multiple items are selected */}
            {multipleSelected && generateStackCards()}

            {/* Main card */}
            <div
              className={`relative flex items-center gap-2.5 rounded-lg border px-3 py-2 shadow-lg ${
                multipleSelected ? "border-blue-200 bg-blue-50" : "border-gray-200 bg-white"
              }`}
            >
              <div
                className={`flex h-7 w-7 items-center justify-center rounded-md ${
                  multipleSelected ? "bg-blue-100" : "bg-gray-50"
                }`}
              >
                <div className={multipleSelected ? "text-blue-600" : "text-gray-600"}>
                  <DragItemIcon multipleSelected={multipleSelected} activeItem={activeItem} />
                </div>
              </div>

              <div className="flex flex-col">
                <div className="flex items-center gap-1.5">
                  <span
                    className={`max-w-[180px] truncate text-sm font-medium ${
                      multipleSelected ? "text-blue-900" : "text-gray-900"
                    }`}
                  >
                    {multipleSelected ? `${selectedCount} items` : activeItem.name}
                  </span>
                  {!multipleSelected && itemCount !== undefined && (
                    <span className="rounded-md bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
                      {itemCount}
                    </span>
                  )}
                </div>
                <span className={`text-xs ${multipleSelected ? "text-blue-600" : "text-gray-500"}`}>
                  {multipleSelected ? "Multiple selected" : getEntityName(activeItem.type)}
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (typeof document !== "undefined") {
    return createPortal(overlayContent, document.body);
  }

  return null;
};

export default CustomDragOverlay;
